import type { MutableRefObject, ReactNode, RefObject } from 'react';

import type {
	Cell,
	Column,
	ColumnOrderState,
	Header,
	Row,
	Row as RowType,
	RowData,
	Table,
	TableOptions,
	Row as TanRow,
	TableState as TanTableState,
	Updater,
	useReactTable,
} from '@tanstack/react-table';
import type { PartialKeys, Virtualizer, VirtualizerOptions } from '@tanstack/react-virtual';

import { type StrictXCSSProp, type XCSSAllProperties, type XCSSAllPseudos } from '@atlaskit/css';
import { type BoxProps } from '@atlaskit/primitives/compiled';

import { type TableState } from './charlie-table';

/**
 * Context provided to the `onColumnOrderChange` callback to indicate how the reorder was triggered.
 * Consumers can use this for analytics without the table needing to know about any specific
 * analytics implementation.
 */
export type ColumnReorderContext = {
	trigger: 'drag' | 'menu';
};

declare module '@tanstack/react-table' {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TData is required to match TanStack's interface signature for declaration merging
	interface ColumnMeta<TData extends RowData, TValue> {
		description?: ReactNode;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TData is required to match TanStack's interface signature for declaration merging
	interface TableOptionsResolved<TData extends RowData> {
		onColumnOrderChange?: (
			updater: Updater<ColumnOrderState>,
			context?: ColumnReorderContext,
		) => void;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TData is required to match TanStack's interface signature for declaration merging
	interface Table<TData extends RowData> {
		/**
		 * Sets the column order and notifies the consumer via `onColumnOrderChange` with an optional
		 * analytics context indicating how the reorder was triggered ('drag' or 'menu').
		 *
		 * - In controlled mode (consumer provides `onColumnOrderChange`): calls the consumer's callback
		 *   with the context, matching TanStack's controlled contract.
		 * - In uncontrolled mode: falls back to TanStack's built-in `setColumnOrder`.
		 */
		setColumnOrderWithContext: (
			updater: Updater<ColumnOrderState>,
			context?: ColumnReorderContext,
		) => void;
	}
}

/**
 * BodyCellBackground is a type-safe enum for the background colors of table cells.
 * This is used to determine the background color of a cell based on its state.
 */
export const BodyCellBackground = {
	success: 'success',
	danger: 'danger',
	information: 'information',
	discovery: 'discovery',
	warning: 'warning',
	selected: 'selected',
	disabled: 'disabled',
} as const;
export type BodyCellBackground = (typeof BodyCellBackground)[keyof typeof BodyCellBackground];
/**
 * type defining the spacing for table body rows
 */
export type RowSpacing = 'compact' | 'default';

/**
 * Determines when column sizing state is updated during resize:
 * - onChange: Updates state on every pixel during resize (default, existing behavior)
 * - (WIP) onEnd: Only updates state once when resize is complete (only triggers onColumnSizingChange once per resize operation)
 * - onChangeEnd: Updates state on every pixel during resize (smooth visual) but only triggers onColumnSizingChange once when resize is complete
 * @link [API Docs](https://tanstack.com/table/latest/docs/framework/react/examples/column-sizing)
 */
export type ColumnResizeMode = 'onChange' | 'onEnd' | 'onChangeEnd';

/**
 * Type-safe enum for Table Types. Naming from: https://hello.atlassian.net/wiki/spaces/CLOUDADMIN/pages/3888095386/Typology+of+Tables
 */
export const TableType = { inventory: 'INVENTORY', input: 'INPUT' } as const;
export type TableType = (typeof TableType)[keyof typeof TableType];
export type RowVirtualizer<
	TScrollElement extends Element | Window = HTMLDivElement,
	TItemElement extends Element = Element,
> = Virtualizer<TScrollElement, TItemElement>;

export type ColumnActionCustomization = {
	/**
	 * The icon to display before the column action.
	 */
	iconBefore?: ReactNode;
	/**
	 * The icon to display after the column action.
	 */
	iconAfter?: ReactNode;
};

/**
 * Configuration object for column actions menu options.
 */
export type ColumnActionsConfig = {
	/**
	 * Enables pinning columns to the left of the table in the column actions menu.
	 * - If `pinToLeft` is `true` then the user can pin columns to the left of the table using the column actions menu.
	 * - If `pinToLeft` is `false` then the user cannot pin columns to the left of the table using the column actions menu.
	 */
	pinToLeft?: boolean | ColumnActionCustomization;
	/**
	 * Enables unpinning columns from the left of the table in the column actions menu.
	 * - If `unpin` is `true` then the user can clear the pinning of columns using the column actions menu.
	 * - If `unpin` is `false` then the user cannot clear the pinning of columns using the column actions menu.
	 */
	unpin?: boolean | ColumnActionCustomization;
	/**
	 * Enables moving columns to the front of the table in the column actions menu.
	 * - If `moveToFront` is `true` then the user can move columns to the front of the table using the column actions menu.
	 * - If `moveToFront` is `false` then the user cannot move columns to the front of the table using the column actions menu.
	 */
	moveToFront?: boolean | ColumnActionCustomization;
	/**
	 * Enables moving columns to the back of the table in the column actions menu.
	 * - If `moveToBack` is `true` then the user can move columns to the back of the table using the column actions menu.
	 * - If `moveToBack` is `false` then the user cannot move columns to the back of the table using the column actions menu.
	 */
	moveToBack?: boolean | ColumnActionCustomization;
	/**
	 * Enables hide column option in the column actions menu.
	 * - If `hiding` is `true` then the user can hide columns.
	 * - If `hiding` is `false` then the user cannot hide columns.
	 */
	hiding?: boolean | ColumnActionCustomization;
	/**
	 * Enables move column right option in the column actions menu.
	 * - If `moveRight` is `true` then the user can move columns to the right.
	 * - If `moveRight` is `false` then the user cannot move columns to the right.
	 */
	moveRight?: boolean | ColumnActionCustomization;
	/**
	 * Enables move column left option in the column actions menu.
	 * - If `moveLeft` is `true` then the user can move columns to the left.
	 * - If `moveLeft` is `false` then the user cannot move columns to the left.
	 */
	moveLeft?: boolean | ColumnActionCustomization;
	/**
	 * Enables sort column option in the column actions menu.
	 * - If `sorting` is `true` then the user can sort columns.
	 * - If `sorting` is `false` then the user cannot sort columns.
	 */
	sortAscending?: boolean | ColumnActionCustomization;
	/**
	 * Enables sort column descending option in the column actions menu.
	 * - If `sortDescending` is `true` then the user can sort columns descending.
	 * - If `sortDescending` is `false` then the user cannot sort columns descending.
	 */
	sortDescending?: boolean | ColumnActionCustomization;
	/**
	 * Enables clear sorting option in the column actions menu.
	 * - If `clearSorting` is `true` then the user can clear sorting.
	 * - If `clearSorting` is `false` then the user cannot clear sorting.
	 */
	clearSorting?: boolean | ColumnActionCustomization;
};

/**
 * CharlieTableProps is the props for the CharlieTable component.
 * Type T should be an Entity type that is used in the table.
 */
export type CharlieTableProps<T extends object> = Pick<
	TableOptions<T>,
	| 'enableRowSelection'
	| 'enableMultiRowSelection'
	| 'enableSubRowSelection'
	| 'onGroupingChange'
	| 'onSortingChange'
	| 'onExpandedChange'
	| 'onRowSelectionChange'
	| 'onColumnSizingChange'
	| 'onColumnPinningChange'
	| 'onColumnVisibilityChange'
	| 'getRowId'
	| 'defaultColumn'
	| 'groupedColumnMode'
	| 'getSubRows'
	| 'getRowCanExpand'
	| '_features'
	| 'sortDescFirst'
	| 'manualGrouping'
	| 'manualExpanding'
	| 'manualFiltering'
	| 'manualPagination'
	| 'enableSortingRemoval'
	| 'enableSubRowSelection'
> & {
	// tanstack duplication to be deleted

	/**
	 * Callback fired when the column order changes due to a user-initiated reorder (drag or menu action).
	 * The optional `context` parameter indicates how the reorder was triggered, which can be used
	 * for analytics without coupling the table to any specific analytics implementation.
	 *
	 * @example
	 * onColumnOrderChange={(order, context) => {
	 *   setColumnOrder(order);
	 *   if (context) {
	 *     analytics.track('column_reordered', { trigger: context.trigger });
	 *   }
	 * }}
	 */
	onColumnOrderChange?: (
		updater: Updater<ColumnOrderState>,
		context?: ColumnReorderContext,
	) => void;

	data?: TableOptions<T>['data'];

	/**
	 * Not all of tanstack State is fully implemented here and it may not do anything until implemented
	 */
	state?: Partial<TanTableState>;

	/**
	 * Not all of tanstack State is fully implemented here and it may not do anything until implemented
	 */
	initialState?: Partial<TanTableState>;

	/**
	 * Enables/disables row reordering via pragmatic drag + drop.
	 * When true, onDraggingRowEnd callback is required.
	 * @experimental
	 */
	enableRowReorder?: boolean;

	/**
	 * A callback that is called when a row is dragged and dropped.
	 * Required when enableRowReorder is true
	 * @param table the table object
	 * @param draggingRow the row that is being dragged
	 * @param destinationRow the row that the dragging row is being dropped on
	 * @returns void
	 */
	onDraggingRowEnd?: ({
		table,
		draggingRow,
		destinationRow,
	}: {
		table: Table<T>;
		draggingRow: Row<T>;
		destinationRow: Row<T>;
	}) => void;

	columnDefs?: TableOptions<T>['columns'];

	/**
	 * renderChildComponent is a function that allows for sub-rows to be rendered.
	 * This is useful for rendering nested rows or rows that are not directly related to the parent row. (IE grouping)
	 */
	renderChildComponent?: ({
		rowData,
		tableState,
	}: {
		rowData: TanRow<T>;
		tableState: TanTableState;
	}) => ReactNode;

	//end duplication

	/**
	 * testId for the <table> component
	 */
	testId?: string;
	/**
	 * Determine if columns can be resized via pragmatic drag + drop
	 */
	enableResizing?: boolean;
	/**
	 * Determines when column sizing state is updated during resize:
	 * - onChange: Updates state on every pixel during resize (default, existing behavior)
	 * - (WIP) onEnd: Only updates state once when resize is complete (only triggers onColumnSizingChange once per resize operation)
	 * - onChangeEnd: Updates state on every pixel during resize (smooth visual) but only triggers onColumnSizingChange once when resize is complete
	 * @link [API Docs](https://tanstack.com/table/latest/docs/framework/react/examples/column-sizing)
	 */
	columnResizeMode?: ColumnResizeMode;
	/**
	 * Determine if columns can be reordered via pragmatic drag + drop.
	 */
	enableColumnReorder?: boolean;
	/**
	 * onLoadNext is used for infinite scrolling.
	 * This function should be called when the table triggers a "load more state". Such as when the user scrolls to the bottom of the table.
	 * @returns void
	 */
	onLoadNext?: () => void;
	/**
	 * hasNext is used for infinite scrolling. This boolean should be true if there are more rows to load.
	 */
	hasNext?: boolean;

	/**
	 * Contains the refs for certain table elements.
	 */
	refs?: {
		/**
		 * tableRef is a ref for the <Table> element
		 */
		tableRef?: RefObject<HTMLTableElement>;
		/**
		 * tableBodyRef is a ref for the <TBody> element.
		 */
		tableBodyRef?: RefObject<HTMLTableSectionElement>;
		/**
		 * tableContainerRef is a ref for the <Box> that surrounds the <Table>
		 */
		tableContainerRef?: RefObject<HTMLDivElement>;
		/**
		 * rowVirtualizerRef is a ref for the <Virtualizer> element.
		 * This helps us scroll to the top of the table when virtualizing and something changes
		 */
		rowVirtualizerRef?: MutableRefObject<RowVirtualizer | null>;
		/**
		 * internalTableRef is a ref for internal tanstack table state.
		 */
		internalTableRef?: React.RefObject<ReturnType<typeof useReactTable<T>> | null>;
	};

	/**
	 * tableState is used to set the current state of the table.
	 * It can take on values as defined in the TableState enum.
	 */
	tableState?: TableState;

	/**
	 * errorView is used to display an error message when tableState === TableState.error
	 */
	errorView?: ReactNode;
	/**
	 * emptyTableView is used to display a loading message when data is empty and tableState === TableState.loaded
	 */
	emptyTableView?: ReactNode;
	/**
	 * statusBar is rendered in a bar at the bottom of the table (outside the scroll area).
	 * Use for row count ("Showing 8 of 8 rows"), pagination controls, actions, or other summary content.
	 * Renders below the scrollable table and does not scroll horizontally.
	 *
	 * **Important:** The status bar is only shown when the `charlie-table-shape-refresh` feature flag
	 * is enabled. If the flag is disabled, the status bar is a no-op (nothing is rendered).
	 */
	statusBar?: ReactNode;
	/**
	 * Hides the HR divider in the footer of the table that indicates end of pagination
	 */
	hidePaginationEndIndicator?: boolean;

	labels?: {
		/**
		 * The aria-label for the table. Has no default.
		 */
		table?: string;
		/**
		 * The text displayed for the button that loads more data. Defaults to "Load more"
		 */
		loadMore?: string;
		/**
		 * The aria-label for the button that loads more data. Defaults to "Load more rows"
		 */
		loadMoreLabel?: string;
		/**
		 * The aria-label for the sort button. Defaults to "<column header>".
		 * If a function is provided, it will be called with the header and should return a string.
		 */
		sortLabel?: string | ((header: Header<T, unknown>) => string);
		/**
		 * The aria-label for the select all rows button. Defaults to "Select all rows"
		 */
		selectAllRows?: string;
		/**
		 * The aria-label for the select row button. Defaults to the "Select row"
		 */
		selectRow?: string;
		/**
		 * The aria-label for the expand row button. Defaults to "Expand row"
		 */
		expandRow?: string | ((row: TanRow<T>) => string);
		/**
		 * The group label for sort options. Defaults to "Sort options"
		 */
		sortGroup?: string;
		/**
		 * The text displayed for the button that clears sorting. Defaults to "Clear sorting"
		 */
		clearSorting?: string;
		/**
		 * The text displayed for the button that sorts ascending. Defaults to "Sort ascending"
		 */
		sortAscending?: string;
		/**
		 * The text displayed for the button that sorts descending. Defaults to "Sort descending"
		 */
		sortDescending?: string;
		/**
		 * The group label for move column options. Defaults to "Move column options"
		 */
		moveColumnGroup?: string;
		/**
		 * The text displayed for the button that moves a column to the left. Defaults to "Move column to left"
		 */
		moveColumnLeft?: string;
		/**
		 * The text displayed for the button that moves a column to the right. Defaults to "Move column to right"
		 */
		moveColumnRight?: string;
		/**
		 * The text displayed for the button that moves a column to the first position. Defaults to "Move column to first position"
		 */
		moveColumnFirst?: string;
		/**
		 * The text displayed for the button that moves a column to the last position. Defaults to "Move column to last position"
		 */
		moveColumnLast?: string;
		/**
		 * The aria-label for the resize column button. Defaults to "Resize column: <column id>".
		 * If a function is provided, it will be called with the header and should return a string.
		 */
		resizeColumn?: string | ((header: Header<T, unknown>) => string);
		/**
		 * The aria-label for the more actions button. Defaults to "More actions for <column id> column"
		 * If a function is provided, it will be called with the header and should return a string.
		 */
		moreActions?: string | ((header: Header<T, unknown>) => string);
		/**
		 * The group label for column visibility options (pin, unpin, hide). Defaults to "Column visibility options"
		 */
		columnVisibilityGroup?: string;
		/**
		 * The text displayed for the button that pins a column to the left. Defaults to "Pin column"
		 */
		pinColumn?: string;
		/**
		 * The text displayed for the button that unpins a column from the left. Defaults to "Unpin column"
		 */
		unpinColumn?: string;
		/**
		 * The text displayed for the button that hides a column from the table. Defaults to "Hide column"
		 */
		hideColumn?: string;
		/**
		 * The text displayed for the loading more message
		 * @default "Loading more"
		 */
		loadingMore?: string;
	};
	onRowClick?: (row: RowType<T>) => void;
	/**
	 * Additional information to be included in the `context` of Atlaskit analytics events that come from CharlieTable
	 */
	analyticsContext?: Record<string, any>;
	/**
	 * An optional name used to identify events for [React UFO (Unified Frontend Observability) press interactions](https://developer.atlassian.com/platform/ufo/react-ufo/react-ufo/getting-started/#quick-start--press-interactions). For more information, see [React UFO integration into Design System components](https://go.atlassian.com/react-ufo-dst-integration).
	 */
	interactionName?: string;
	rowSpacing?: RowSpacing;
	/**
	 * An optional prop that will prevent a tanstack table from resetting the expanded state when the data changes; applicable when
	 * a table with expandable rows lazy loads additional data
	 */
	autoResetExpanded?: boolean;

	renderOptions?: {
		groupingRow?: (rowData: T, state: TableOptions<T>['state']) => ReactNode;
		bodyCellBackground?: ({
			cell,
			state,
		}: {
			cell: Cell<T, unknown>;
			state: TableOptions<T>['state'];
		}) => BodyCellBackground | undefined;
		/**
		 * Optional function to determine the background color for grouped rows.
		 * Returns a BodyCellBackground color option that will be applied to the grouped row.
		 */
		groupedRowBackground?: ({
			rowData,
			state,
		}: {
			rowData: T;
			state: TableOptions<T>['state'];
		}) => BodyCellBackground | undefined;
		/**
		 * Configurable padding options for expandable row indentation
		 */
		expandableRow?: {
			/**
			 * Base padding unit in pixels for each depth level. Defaults to 8px.
			 */
			depthUnit?: number;
			/**
			 * Whether to add extra padding to non-expandable rows to align them with expandable row content.
			 * When true, adds padding equal to depthUnit to non-expandable rows.
			 * Defaults to false.
			 */
			alignNonExpandableRows?: boolean;
		};
	};

	/**
	 * Defaults to true. Disable if you prefer tanstack to sort for you
	 */
	manualSorting?: TableOptions<T>['manualSorting'];

	enableServerSideGrouping?: boolean;

	/**
	 * Enables/Disables and configures the column actions menu.
	 *
	 * Supports multiple formats:
	 * - `boolean`: Enable/disable column actions for all columns with default configuration
	 * - `ColumnActionsConfig`: Enable column actions for all columns with custom configuration
	 * - `(column) => boolean`: Conditionally enable/disable column actions per column with default configuration
	 * - `(column) => ColumnActionsConfig`: Conditionally configure column actions per column
	 * - `(column) => boolean | ColumnActionsConfig`: Conditionally enable/disable or configure per column
	 *
	 * @example
	 * // Enable column actions for all columns with default configuration
	 * enableColumnActions={true}
	 *
	 * @example
	 * // Disable column actions for all columns
	 * enableColumnActions={false}
	 *
	 * @example
	 * // Enable column actions with custom configuration for all columns
	 * enableColumnActions={{ pinToLeft: true, hiding: true, moveToFront: false }}
	 *
	 * @example
	 * // Conditionally enable column actions for specific columns
	 * enableColumnActions={(column) => column.id !== 'readOnlyColumn'}
	 *
	 * @example
	 * // Conditionally configure column actions per column
	 * enableColumnActions={(column) => {
	 *   if (column.id === 'name') return { pinToLeft: true, hiding: false };
	 *   if (column.id === 'status') return true;
	 *   return false;
	 * }}
	 */
	enableColumnActions?:
		| boolean
		| ColumnActionsConfig
		| ((column: Column<T, unknown>) => boolean | ColumnActionsConfig);

	/**
	 * @deprecated Use `enableColumnActions` instead. This prop will be removed in a future version.
	 *
	 * Configures the column actions menu.
	 * For new code, pass the configuration object directly to `enableColumnActions`.
	 */
	columnActions?: ColumnActionsConfig;

	/**
	 * Enables Row Virtualization for the table.
	 * Note: This disables the aggressive table body memoization unless resizing
	 * @experimental
	 */
	enableRowVirtualization?: boolean;

	rowVirtualizerOptions?:
		| (({
				table,
		  }: {
				table: Table<T>;
		  }) => PartialKeys<
				VirtualizerOptions<HTMLDivElement, Element>,
				'observeElementRect' | 'observeElementOffset' | 'scrollToFn'
		  >)
		| PartialKeys<
				VirtualizerOptions<HTMLDivElement, Element>,
				'observeElementRect' | 'observeElementOffset' | 'scrollToFn'
		  >;

	/**
	 * Enables Column Virtualization for the table.
	 * @experimental
	 */
	enableColumnVirtualization?: boolean;

	columnVirtualizerOptions?:
		| (({
				table,
		  }: {
				table: Table<T>;
		  }) => PartialKeys<
				VirtualizerOptions<HTMLDivElement, Element>,
				'observeElementRect' | 'observeElementOffset' | 'scrollToFn'
		  >)
		| PartialKeys<
				VirtualizerOptions<HTMLDivElement, Element>,
				'observeElementRect' | 'observeElementOffset' | 'scrollToFn'
		  >;

	tableType?: TableType;

	/**
	 * An optional parameter used to override XCSS styles for table header (<th>) cells.
	 */
	headerCellXcss?: StrictXCSSProp<XCSSAllProperties, XCSSAllPseudos>;
	/**
	 * An optional parameter used to override XCSS styles for table data (<td>) cells.
	 */
	rowCellXcss?: StrictXCSSProp<XCSSAllProperties, XCSSAllPseudos>;
	/**
	 * An optional parameter used to override XCSS styles for the table wrapper (<div>) element.
	 */
	tableWrapperXcss?: StrictXCSSProp<Exclude<XCSSAllProperties, 'background'>, XCSSAllPseudos>;

	/**
	 * Allows for tables to use keyboard arrow
	 * keys (up, down, left, right) or tabbing to navigate the table.
	 *
	 * The hook also includes meta + c to copy the content. Since the
	 * table is not ADF, this only copies text content and not the
	 * full experience like images / status lozenge.
	 */
	enableKeyboardNavigation?: boolean;

	/**
	 * Optional caption for the table.
	 * If a function is provided, the <caption> element will not be shown and the consumer is responsible for rendering the caption.
	 */
	tableCaption?: string | (() => ReactNode);

	/**
	 * Optional function that allows the consumer to override the default responsive column sizing.
	 * This is useful for a few situations such as:
	 * - Making the last non-pinned column grow instead of the pinned columns (like an action column)
	 * - Making all columns the same size (similar to how charlieTable used to behave )
	 *
	 * When placed on a column on the left side of the table, column resizing will have styling regressions due to how flexboxes work.
	 * We recommend placing this on a column at the end of the non-pinned columns.
	 * @param column the Tanstack column object
	 * @returns true if the column should be responsive, false if it should not be responsive
	 */
	responsiveColumnSizing?: (column: Column<T, unknown>) => boolean;

	/**
	 * Optional component prop overrides for internal table components.
	 */

	componentPropOverrides?: {
		row?: ({ row }: { row: RowType<T> }) => Exclude<BoxProps<'tr'>, 'xcss'>;
	};
};
